import ReactPixel from 'react-facebook-pixel';

const MIXPANEL_TOKEN =
  process.env.REACT_APP_MIXPANEL_TOKEN || process.env.MIXPANEL_TOKEN || '09b3e02ea7b56dc4edc4b3b5a64f386c';
const MIXPANEL_REGION = process.env.REACT_APP_MIXPANEL_REGION || process.env.MIXPANEL_REGION || 'api';
const MIXPANEL_SCRIPT_SRC = 'https://cdn.mxpnl.com/libs/mixpanel-2-latest.min.js';
const MIXPANEL_API_HOST = `https://${MIXPANEL_REGION}.mixpanel.com`;
const MIXPANEL_LOAD_TIMEOUT_MS = 8000;

let mixpanelLoaderPromise = null;
let mixpanelInitialized = false;

const logDebug = (...args) => {
  // Use console.log so it is visible even if info/debug are filtered
  // eslint-disable-next-line no-console
  console.log('[analytics]', ...args);
};

const resolveUserId = (eventParams) => {
  const payload = eventParams || {};
  const fromPayload = payload.user_id || payload.userId || payload.distinct_id;
  if (fromPayload) return String(fromPayload);

  try {
    const fromStorage =
      localStorage.getItem('user_id') ||
      localStorage.getItem('userId');
    if (fromStorage) return String(fromStorage);
  } catch (e) {
    // ignore
  }

  // Try to extract user_id from JWT token in cookie (access_token)
  try {
    const token = document.cookie
      .split('; ')
      .find(row => row.startsWith('access_token='))
      ?.split('=')[1];
    if (token) {
      const parts = token.split('.');
      if (parts.length === 3) {
        const tokenPayload = JSON.parse(atob(parts[1]));
        const userId = tokenPayload.user_id || tokenPayload.userId || tokenPayload.sub || tokenPayload.id;
        if (userId) return String(userId);
      }
    }
  } catch (e) {
    // ignore JWT parsing errors
  }

  try {
    const params = new URLSearchParams(window.location.search);
    const fromQuery = params.get('user_id') || params.get('userId');
    if (fromQuery) return String(fromQuery);
  } catch (e) {
    // ignore
  }

  return null;
};

const withUserIdProps = (eventParams) => {
  const props = eventParams ? { ...eventParams } : {};
  const userId = resolveUserId(props);
  if (userId) {
    props.user_id = userId;
    props.distinct_id = userId;
  }
  return { props, userId };
};

const loadMixpanelScript = () => {
  if (typeof window === 'undefined') return Promise.resolve(null);
  if (window.mixpanel) return Promise.resolve(window.mixpanel);
  if (mixpanelLoaderPromise) return mixpanelLoaderPromise;

  mixpanelLoaderPromise = new Promise((resolve) => {
    const script = document.createElement('script');
    script.src = MIXPANEL_SCRIPT_SRC;
    script.async = true;
    script.onload = () => resolve(window.mixpanel || null);
    script.onerror = () => {
      console.warn('Mixpanel script failed to load');
      resolve(null);
    };

    const timer = setTimeout(() => {
      console.warn('Mixpanel script load timed out');
      resolve(null);
    }, MIXPANEL_LOAD_TIMEOUT_MS);

    script.addEventListener('load', () => clearTimeout(timer));
    script.addEventListener('error', () => clearTimeout(timer));

    document.head.appendChild(script);
  });

  return mixpanelLoaderPromise;
};

const initMixpanel = async () => {
  if (mixpanelInitialized) return window.mixpanel || null;
  const mixpanel = await loadMixpanelScript();
  if (!mixpanel || !MIXPANEL_TOKEN) return null;

  try {
    // Create an instance of the Mixpanel object with debug and autocapture enabled
    mixpanel.init(MIXPANEL_TOKEN, {
      debug: true, // enable debug log in console
      autocapture: true, // enable autocapture
      api_host: MIXPANEL_API_HOST,
    });

    // disable debug log after initialization
    mixpanel.set_config({
      debug: false,
    });

    mixpanelInitialized = true;
    // Helpful console signal during debugging
    logDebug('Mixpanel initialized');
  } catch (err) {
    console.warn('Mixpanel initialization failed', err);
    return null;
  }

  return mixpanel;
};

const sendToMixpanel = (eventName, eventParams) => {
  const { props, userId } = withUserIdProps(eventParams);
  initMixpanel()
    .then((mixpanel) => {
      if (!mixpanel) {
        console.warn('Mixpanel not available; event not sent', eventName, props);
        return;
      }
      try {
        if (userId && mixpanel.get_distinct_id && mixpanel.get_distinct_id() !== userId) {
          mixpanel.identify(userId);
        }
        logDebug('Mixpanel track ->', eventName, props || {});
        mixpanel.track(eventName, props || {});
      } catch (err) {
        console.warn('Mixpanel track failed', err);
      }
    })
    .catch((err) => {
      console.warn('Mixpanel init failed', err);
    });
};

const sendToMixpanelDirect = (eventName, eventParams) => {
  // Fallback direct POST if SDK is blocked; best-effort only.
  try {
    const { props, userId } = withUserIdProps(eventParams);
    const distinctId = userId || 'web_fallback';
    const payload = {
      event: eventName,
      properties: {
        token: MIXPANEL_TOKEN,
        distinct_id: distinctId,
        time: Date.now(),
        ...props,
      },
    };
    const body = `data=${btoa(JSON.stringify(payload))}`;
    logDebug('Mixpanel direct track ->', eventName, props || {});
    fetch(`${MIXPANEL_API_HOST}/track`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body,
    }).catch((err) => console.warn('Mixpanel direct track failed', err));
  } catch (err) {
    console.warn('Mixpanel direct track failed to build payload', err);
  }
};

const mirrorToMixpanel = (eventName, eventParams) => {
  // Try SDK first, then fallback direct if SDK not available within 1s
  let sent = false;
  sendToMixpanel(eventName, eventParams);
  setTimeout(() => {
    if (window.mixpanel) return;
    if (sent) return;
    sent = true;
    sendToMixpanelDirect(eventName, eventParams);
  }, 1000);
};

const trackEvent = (eventName, eventParams) => {
    let paramsObj;
    if (eventParams === undefined) {
        // If no params provided, use URL search params
        const urlObj = new URL(window.location.href);
        const params = new URLSearchParams(urlObj.search);
        paramsObj = {};
        params.forEach((value, key) => {
            paramsObj[key] = value;
        });
    } else {
        paramsObj = eventParams;
    }

    // Ensure user_id/distinct_id is attached everywhere
    const { props: paramsWithUser } = withUserIdProps(paramsObj);

    if (window.gtag) {
        window.gtag('event', eventName, paramsWithUser);
    } else {
        console.warn('Google Analytics not loaded');
    }

    // Send to Facebook Pixel using ReactPixel
    try {
        ReactPixel.track(eventName, paramsWithUser);
    } catch (err) {
        // Silently ignore to avoid breaking web
        console.warn('Facebook Pixel event tracking failed', err);
    }

    // Also send to Android bridge if available
    try {
        if (window.AndroidBridge) {
            const payload = paramsWithUser ? JSON.stringify(paramsWithUser) : '{}';
            window.AndroidBridge.logEvent(eventName, payload);
        }
    } catch (err) {
        // Silently ignore to avoid breaking web
        console.warn('AndroidBridge.logEvent failed', err);
    }

    // Helpful console log to confirm events are being fired (acts like a "print" in terminal)
    logDebug('trackEvent ->', eventName, paramsWithUser);

    // Mirror events to Mixpanel
    mirrorToMixpanel(eventName, paramsWithUser);
};

export default trackEvent;
